import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { eventsAPI } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import { useToast } from '../../context/ToastContext';
import { useLanguage } from '../../context/LanguageContext';
import './Pos.css';

const emptyForm = {
  title: '',
  description: '',
  event_date: '',
  location: '',
  address: '',
  max_participants: 0,
  registration_deadline: '',
  require_registration: true,
  allow_walk_in: false,
  require_verified: false,
  eligible_categories: '',
  gift_batch: '',
  status: 'upcoming',
  is_published: true,
};

function toLocalInput(value) {
  if (!value) return '';
  return String(value).replace(' ', 'T').slice(0, 16);
}

const EventForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { t } = useLanguage();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    eventsAPI.adminGet(id)
      .then((response) => {
        const event = response.data;
        setForm({
          ...emptyForm,
          ...event,
          event_date: toLocalInput(event.event_date),
          registration_deadline: toLocalInput(event.registration_deadline),
          gift_batch: event.gift_batch || '',
          address: event.address || '',
        });
      })
      .catch(() => error('找不到活動'))
      .finally(() => setLoading(false));
  }, [id, error]);

  const setField = (name, value) => setForm((current) => ({ ...current, [name]: value }));

  const onSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        max_participants: Number(form.max_participants) || 0,
        gift_batch: form.gift_batch || null,
        registration_deadline: form.registration_deadline || null,
        event_date: form.event_date,
      };
      delete payload.registered_count;
      delete payload.attendance_count;
      delete payload.is_registration_open;
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.image;
      if (id) {
        await eventsAPI.adminUpdate(id, payload);
      } else {
        await eventsAPI.adminCreate(payload);
      }
      success('活動已儲存');
      navigate('/admin/events');
    } catch (err) {
      error('活動儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingPage message="載入活動..." />;

  return (
    <div className="pos-card">
      <p><Link to="/admin/events">返回活動</Link></p>
      <h1>{id ? '編輯活動' : '新增活動'}</h1>
      <form className="pos-form-grid" onSubmit={onSubmit}>
        <label>標題
          <input value={form.title} onChange={(e) => setField('title', e.target.value)} required />
        </label>
        <label>描述
          <textarea value={form.description} onChange={(e) => setField('description', e.target.value)} required />
        </label>
        <label>日期時間
          <input type="datetime-local" value={form.event_date} onChange={(e) => setField('event_date', e.target.value)} required />
        </label>
        <label>地點
          <input value={form.location} onChange={(e) => setField('location', e.target.value)} required />
        </label>
        <label>地址
          <input value={form.address} onChange={(e) => setField('address', e.target.value)} />
        </label>
        <label>人數上限（0 表示不限）
          <input type="number" min="0" value={form.max_participants} onChange={(e) => setField('max_participants', e.target.value)} />
        </label>
        <label>報名截止
          <input type="datetime-local" value={form.registration_deadline} onChange={(e) => setField('registration_deadline', e.target.value)} />
        </label>
        <label>狀態
          <select value={form.status} onChange={(e) => setField('status', e.target.value)}>
            <option value="upcoming">即將舉行</option>
            <option value="ongoing">進行中</option>
            <option value="completed">已完成</option>
            <option value="cancelled">已取消</option>
          </select>
        </label>
        <label className="pos-check">
          <input type="checkbox" checked={form.require_registration} onChange={(e) => setField('require_registration', e.target.checked)} />
          需要報名
        </label>
        <label className="pos-check">
          <input type="checkbox" checked={form.allow_walk_in} onChange={(e) => setField('allow_walk_in', e.target.checked)} />
          允許現場報名
        </label>
        <label className="pos-check">
          <input type="checkbox" checked={form.require_verified} onChange={(e) => setField('require_verified', e.target.checked)} />
          只限已驗證會員
        </label>
        <label>{t('events.categories')}
          <input value={form.eligible_categories} onChange={(e) => setField('eligible_categories', e.target.value)} />
        </label>
        <div className="pos-actions">
          <button className="pos-btn" type="submit" disabled={saving}>{saving ? '儲存中...' : '儲存'}</button>
        </div>
      </form>
    </div>
  );
};

export default EventForm;
